import { Controller } from 'stimulus';

// Form-level controller that visually marks every invalid required field
// when the form is submitted, so all missing fields get a red border and
// inline "Required" hint at once — not just the first one the browser
// happens to anchor its native tooltip to.
//
// For TomSelect-wrapped selects the underlying <select> is visually
// hidden (`.ts-hidden-accessible` — `position:absolute; width:1px;
// clip:rect(0 0 0 0)`), so the native popup would land on a 1px ghost.
// For those we still suppress the popup and decorate the visible
// `.ts-wrapper` instead. For ordinary inputs we leave the browser's
// tooltip alone — it already points at a visible element.

export default class extends Controller {
  connect() {
    this._onInvalid = this._onInvalid.bind(this);
    this._onInput   = this._onInput.bind(this);
    this.element.addEventListener('invalid', this._onInvalid, true);
    this.element.addEventListener('input',   this._onInput,   true);
    this.element.addEventListener('change',  this._onInput,   true);
  }

  disconnect() {
    this.element.removeEventListener('invalid', this._onInvalid, true);
    this.element.removeEventListener('input',   this._onInput,   true);
    this.element.removeEventListener('change',  this._onInput,   true);
  }

  _tomWrapperFor(select) {
    const sibling = select.nextElementSibling;
    if (sibling && sibling.classList.contains('ts-wrapper')) return sibling;
    return null;
  }

  _anchorFor(target) {
    if (target.tagName === 'SELECT') {
      const wrapper = this._tomWrapperFor(target);
      if (wrapper) return wrapper;
    }
    return target;
  }

  // Multi-select field boxes (`.w-full ... shadow-sm` wrappers in
  // `_builtin_form_field`) cast their drop-shadow below the marker if we
  // append it inside the box. Hoist the marker to be a sibling of that box
  // instead, so the shadow stops at the widget's bottom edge.
  _markerHostFor(anchor) {
    const shadowBox = anchor.closest('.shadow-sm');
    return (shadowBox && this.element.contains(shadowBox)) ? shadowBox : anchor;
  }

  _onInvalid(event) {
    const target = event.target;
    if (!target || !['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;

    const anchor = this._anchorFor(target);

    // Only suppress the native popup when it would point at a hidden
    // TomSelect ghost; visible fields keep their normal browser tooltip.
    if (anchor !== target || target.classList.contains('tomselected')) {
      event.preventDefault();
    }

    anchor.classList.add('has-required-error');

    const host = this._markerHostFor(anchor);
    const next = host.nextElementSibling;
    if (!next || !next.classList.contains('required-form-error')) {
      const errorEl = document.createElement('div');
      errorEl.className = 'required-form-error';
      errorEl.textContent = 'Required';
      host.insertAdjacentElement('afterend', errorEl);
    }

    if (!this._firstInvalidAnchor) {
      this._firstInvalidAnchor = anchor;
      Promise.resolve().then(() => {
        const a = this._firstInvalidAnchor;
        this._firstInvalidAnchor = null;
        if (a) a.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }

  _onInput(event) {
    const target = event.target;
    if (!target || !['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;

    const anchor = this._anchorFor(target);
    if (!anchor.classList.contains('has-required-error')) return;
    if (typeof target.checkValidity === 'function' && !target.checkValidity()) return;

    anchor.classList.remove('has-required-error');
    const host = this._markerHostFor(anchor);
    const next = host.nextElementSibling;
    if (next && next.classList.contains('required-form-error')) next.remove();
  }
}
